import { DateTime } from 'luxon';
import { Tour, tourJson } from './tour.model';
import { immerable } from "immer";
import { TFunction } from 'gatsby-plugin-react-i18next';

export interface ToursCalendarState {
    /**
     * Available dates for booking in tours calendar for particular days range 
     * (default range is from today unit last day of next month)
     */
    upcomingTourDates?: UpcomingTourDates,
    /**
     * Available tours for booking for particular day
     */
    dayAvailableTours?: DayAvailableTours,

    cachedDaysAvailableTours: DayAvailableTours[],

    daySelected?: DateTime,
    translate?: TFunction
    previewPass?: string,
    /**
     * Project id filter
     */
    project?: string
}

/**
 * Dates available for booking.
 * Used by calendar.
 */
export class UpcomingTourDates {
    [immerable] = true
    /**
     * Start of dates pagination range.
     */
    from: DateTime;
    /**
     * End of dates pagination range.
     * Used to fetch new dates batch for next months.
     */
    until: DateTime;
    /**
     * Tour dates available for booking
     */
    upcomingToursDates: DateTime[];

    constructor(from: DateTime, until: DateTime,
        upcomingToursDates: DateTime[]) {
        this.from = from;
        this.until = until;
        this.upcomingToursDates = upcomingToursDates;
    }

    static fromJson = function (json: { from: string, until: string, upcomingToursDates: string[] }): UpcomingTourDates {

        return new UpcomingTourDates(DateTime.fromISO(json.from), DateTime.fromISO(json.until), json.upcomingToursDates.map((date) => DateTime.fromISO(date)).sort());
    }

    addDates(utd: UpcomingTourDates): void {
        if (this.until > utd.until) return;
        this.until = utd.until;
        this.upcomingToursDates.push(...utd.upcomingToursDates);
    }

}

/**
 * Base interface for common field properties
 */
interface BaseCustomField {
    id: string;
    displayName: string;
    placeholder?:string;
    required?: boolean;
    helpText?: string;
}

/**
 * Text input custom field
 */
interface TextCustomField extends BaseCustomField {
    type: 'text';
}

/**
 * Select dropdown custom field
 */
interface SelectCustomField extends BaseCustomField {
    type: 'select';
    options: { value: string; label: string }[];
}

/**
 * Union of all possible custom field types
 */
export type FormCustomField = TextCustomField | SelectCustomField;

interface DayAvailableToursJson {
    day: string,
    projectId?: string;
    depositPerTicket?: number;
    ticketsPerOrder?: number;
    tours: TourWithAvailabilityJson[],
    formCustomFields?: FormCustomField[]
}

/**
 * Detailed list of tours with current availability for particular day.
 */
export class DayAvailableTours {

    /**
     * Day for which tours are fetched
     */
    day: DateTime;
    /**
     * Project id for API requests
     */
    projectId?: string;
    /**
    * Deposit value per ticket - from project settings
    */
    depositPerTicket?: number;
    /**
   * Tickets limit per order - from project settings
   */
    ticketsPerOrder?: number;
    tours: TourWithAvailability[];
    formCustomFields?: FormCustomField[];

    constructor(
        day: DateTime,
        tours: TourWithAvailability[],
        projectId?: string,
        depositPerTicket?: number,
        ticketsPerOrder?: number,
        formCustomFields?: FormCustomField[]
    ) {
        this.day = day;
        this.projectId = projectId;
        this.depositPerTicket = depositPerTicket;
        this.ticketsPerOrder = ticketsPerOrder;
        this.tours = tours;
        this.formCustomFields = formCustomFields;
    }

    static fromJson = function (json: DayAvailableToursJson): DayAvailableTours {
        const day = DateTime.fromISO(json.day);
        const tours = json.tours.map(tour => TourWithAvailability.fromJson(tour));
        return new DayAvailableTours(day, tours, json.projectId, json.depositPerTicket, json.ticketsPerOrder, json.formCustomFields);
    }

}

interface TourWithAvailabilityJson {
    tour: tourJson,
    hoursAvailability: {
        hour: string,
        /**
         * Equipment total availability (pcs.)
         */
        availability: number,
        minTickets?: number,
        theme?: Theme,
        price?: number,
        isBookable?: boolean,
        availableFrom?: string
    }[]
}

export interface Theme {
    title: string,
    message: string,
}

export class TourWithAvailability {

    tour: Tour;
    hoursAvailability: {
        hour: DateTime,
        /**
         * Equipment total availability (pcs.)
         */
        availability: Availability,
        /**
         * Minimum no of tickets that must be booked
         */
        minTickets?: number,
        /**
         * Tour theme
         */
        theme: Theme,
        /**
         * Tour's price
         */
        price?: number,
        /**
         * Whether the tour hour is currently bookable
         */
        isBookable?: boolean,
        /**
         * DateTime when the tour hour becomes available for booking
         */
        availableFrom?: DateTime

    }[];

    constructor(tour: Tour,
        hoursAvailability: {
            hour: DateTime,
            /**
             * Equipment total availability (pcs.)
             */
            availability: Availability,
            minTickets?: number,
            theme: Theme,
            price?: number,
            isBookable?: boolean,
            availableFrom?: DateTime
        }[],) {
        this.tour = tour;
        this.hoursAvailability = hoursAvailability;
    }

    static fromJson = function (json: TourWithAvailabilityJson): TourWithAvailability {
        const hoursAvailability = json.hoursAvailability.map((ha) => {
            // WORKAROUND: The API doesn't properly handle DST transitions.
            // Instead of interpreting the UTC timestamp correctly, we extract the time components
            // and reconstruct them in Warsaw timezone to maintain consistent display times.
            // This strips the "Z" (UTC indicator) and parses the datetime as Warsaw local time.

            // Extract the time components from the ISO string (removing the "Z")
            const timeStr = ha.hour.substring(0, 19); // "2025-10-24T14:00:00"

            // Parse as Warsaw time to maintain consistent hour display across DST changes
            const hourWarsaw = DateTime.fromISO(timeStr, { zone: 'Europe/Warsaw' });

            return {
                availability: new Availability(ha.availability),
                hour: hourWarsaw,
                minTickets: ha.minTickets,
                theme: ha.theme,
                price: ha.price,
                isBookable: ha.isBookable,
                availableFrom: ha.availableFrom ? (() => {
                    const availStr = ha.availableFrom.substring(0, 19);
                    return DateTime.fromISO(availStr, { zone: 'Europe/Warsaw' });
                })() : undefined
            }
        });
        return new TourWithAvailability(Tour.fromJson(json.tour), hoursAvailability);
    }

}

export class Availability {
    quantity: number;

    constructor(quantity) {
        this.quantity = quantity;
    }

    get level() {
        if (this.quantity <= 0) return 'none';
        if (this.quantity > 15) return 'major';
        if (this.quantity > 3) return 'good';
        if (this.quantity <= 3) return 'poor';
    }

}

